import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Puzzle, Zap, BookOpenCheck, MessagesSquare, Handshake, Brain, Users, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import ConditionDetailsDialog from "@/components/ConditionDetailsDialog";
import { CONDITION_DETAILS } from "./conditionDetails";

const SUPPORT_PRIMARY = [
  { label: "Autism Spectrum Disorder",   short: "Autism (ASD)",     icon: Puzzle,         accent: "from-coral",         haloBg: "bg-coral/10",         iconColor: "text-coral" },
  { label: "Attention-Deficit/Hyperactivity Disorder", short: "ADHD",          icon: Zap,            accent: "from-amber-500",     haloBg: "bg-amber-100",        iconColor: "text-amber-500" },
  { label: "Learning Disabilities",      short: "Learning Disabilities", icon: BookOpenCheck,  accent: "from-violet-500",    haloBg: "bg-violet-100",       iconColor: "text-violet-500" },
  { label: "Speech & Language Delays",   short: "Speech & Language",     icon: MessagesSquare, accent: "from-pink-500",      haloBg: "bg-pink-100",         iconColor: "text-pink-500" },
  { label: "Social Communication Difficulties", short: "Social Communication", icon: Handshake,      accent: "from-emerald-500",   haloBg: "bg-emerald-100",      iconColor: "text-emerald-500" },
  { label: "Intellectual & Developmental Disabilities", short: "Intellectual & Developmental", icon: Brain, accent: "from-sky-500",       haloBg: "bg-sky-100",          iconColor: "text-sky-500" },
];

const SUPPORT_SECONDARY = [
  { label: "Sensory Processing Difficulties",        dot: "bg-coral" },
  { label: "Developmental Coordination Disorder (Dyspraxia)", dot: "bg-amber-500" },
  { label: "Fine Motor & Handwriting Challenges",    dot: "bg-violet-500" },
  { label: "Gross Motor Delays",                     dot: "bg-pink-500" },
  { label: "Executive Function Difficulties",        dot: "bg-emerald-500" },
  { label: "Emotional Regulation Challenges",        dot: "bg-sky-500" },
  { label: "Behavioural Difficulties",               dot: "bg-coral" },
  { label: "Oppositional Defiant Disorder (ODD)",    dot: "bg-amber-500" },
  { label: "Global Developmental Delay (GDD)",       dot: "bg-violet-500" },
  { label: "Genetic & Rare Developmental Conditions", dot: "bg-sky-500" },
  { label: "Anxiety-Related Challenges",             dot: "bg-coral" },
  { label: "School Readiness Delays",                dot: "bg-amber-500" },
  { label: "Attention & Concentration Difficulties", dot: "bg-violet-500" },
  { label: "Self-Care & Daily Living Skill Delays",  dot: "bg-pink-500" },
  { label: "Play Skill Difficulties",                dot: "bg-emerald-500" },
  { label: "Social Skills Challenges",               dot: "bg-sky-500" },
  { label: "Feeding & Mealtime Difficulties",        dot: "bg-coral" },
  { label: "Children at Risk of Developmental Delays", dot: "bg-amber-500" },
];

/** "Who we support" — 6 primary cards + expandable secondary chips.
 *  Cards and chips are clickable — tapping either opens a details popup
 *  (ConditionDetailsDialog) with the "What is …" / "How Kiddo+ Supports"
 *  copy from ``conditionDetails.js`` and a "Start Free Today" CTA. */
export default function SupportSection() {
  const [showAll, setShowAll] = useState(false);
  // The currently-open condition; ``null`` means the popup is closed.
  // We keep this at the section level so both the primary grid and the
  // secondary chip cloud share a single dialog instance (no z-index
  // stacking, no double-mount of the shadcn Portal).
  const [activeCondition, setActiveCondition] = useState(null);
  const openCondition = (label) => {
    const detail = CONDITION_DETAILS[label];
    if (detail) setActiveCondition(detail);
  };
  return (
    <section className="relative bg-cream kpp-blend-top-white kpp-blend-bottom-white py-16 lg:py-20 overflow-hidden" id="who-we-support">
      <div aria-hidden="true" className="pointer-events-none absolute -top-12 -left-10 w-40 h-40 bg-mint/15 rounded-full blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute top-1/3 -right-16 w-56 h-56 bg-coral/10 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center max-w-3xl mx-auto mb-12 kpp-reveal" data-reveal>
          <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-violet-100 text-violet-700 rounded-full text-[11px] font-bold uppercase tracking-widest mb-5">
            <Heart className="w-3.5 h-3.5" fill="currentColor" strokeWidth={0} /> Who We Support
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-inkdark">
            Many experiences. <span className="text-coral block sm:inline">One place for support.</span>
          </h2>
          <p className="mt-5 text-inkmid text-sm sm:text-base leading-relaxed">
            Kiddo+ is designed for children aged <strong className="text-inkdark">3–12 years</strong> with developmental, learning, behavioural, and neurodivergent needs.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4" data-testid="support-primary-grid">
          {SUPPORT_PRIMARY.map((s) => (
            <button
              key={s.label}
              type="button"
              onClick={() => openCondition(s.label)}
              className="relative bg-white rounded-2xl shadow-soft border border-plum/5 px-3 py-5 sm:py-6 flex flex-col items-center text-center hover-lift overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2 transition-transform"
              title={`${s.label} — tap for details`}
              aria-label={`${s.label} — see how Kiddo+ supports this`}
              data-testid={`support-card-${s.short.toLowerCase().replace(/[^a-z]/gi, '-')}`}
            >
              <span className={`absolute top-0 left-4 right-4 h-1 rounded-b-full bg-gradient-to-r ${s.accent} to-transparent`} />
              <div className={`w-14 h-14 rounded-full ${s.haloBg} flex items-center justify-center mb-3 mt-1`}>
                <s.icon className={`w-6 h-6 ${s.iconColor}`} strokeWidth={2.2} />
              </div>
              <p className="font-heading text-sm sm:text-base leading-tight text-inkdark">{s.short}</p>
            </button>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-center gap-5">
          {!showAll && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              data-testid="support-toggle-all"
              className="inline-flex items-center gap-2 bg-violet-100 hover:bg-violet-200 text-violet-700 rounded-full px-6 py-2.5 text-sm font-bold transition-colors shadow-soft"
            >
              Show all support areas <ArrowRight className="w-4 h-4 rotate-90" />
            </button>
          )}

          {showAll && (
            <>
              <div className="flex flex-wrap justify-center gap-2 sm:gap-2.5 max-w-5xl" data-testid="support-secondary-chips">
                {SUPPORT_SECONDARY.map((c) => (
                  <button
                    key={c.label}
                    type="button"
                    onClick={() => openCondition(c.label)}
                    aria-label={`${c.label} — see how Kiddo+ supports this`}
                    className="inline-flex items-center gap-2 bg-white border border-plum/10 rounded-full px-3 py-1.5 text-xs sm:text-[13px] font-semibold text-inkdark shadow-soft hover:border-coral/40 hover:text-coral focus:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-1 transition-colors"
                    data-testid={`support-chip-${c.label.toLowerCase().replace(/[^a-z]/gi, '-').replace(/-+/g, '-')}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} aria-hidden="true" />
                    {c.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setShowAll(false)}
                data-testid="support-toggle-all"
                className="inline-flex items-center gap-2 bg-violet-100 hover:bg-violet-200 text-violet-700 rounded-full px-5 py-2 text-xs font-bold transition-colors"
              >
                Show less <ArrowRight className="w-3.5 h-3.5 -rotate-90" />
              </button>
            </>
          )}
        </div>

        <div className="mt-10 mx-auto max-w-3xl">
          <div className="bg-violet-50 border border-violet-100 rounded-2xl px-5 py-4 flex items-center gap-3 text-center sm:text-left flex-col sm:flex-row" data-testid="support-reassurance">
            <div className="w-10 h-10 rounded-full bg-white shadow-soft text-violet-600 flex items-center justify-center shrink-0">
              <Users className="w-5 h-5" strokeWidth={2.2} />
            </div>
            <p className="text-sm text-inkdark leading-relaxed">
              <strong>Not sure where your child fits? That&apos;s okay.</strong> Kiddo+ helps you understand your child&apos;s strengths, challenges, and next steps through personalised recommendations developed by child development experts.
            </p>
          </div>
        </div>

        <div className="mt-8 hidden sm:flex justify-center">
          <Link to="/signup">
            <Button
              size="lg"
              className="bg-coral hover:bg-coral-dark text-white rounded-full font-bold px-8 py-6 text-base shadow-soft-md hover-lift"
              data-testid="support-start-free-cta"
            >
              <Sparkles className="w-4 h-4 mr-1" /> Start Free Today <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Condition explainer popup — single instance shared by primary
          grid + secondary chip cloud so the shadcn portal only mounts
          once. Closing sets the condition back to null. */}
      <ConditionDetailsDialog
        open={!!activeCondition}
        onOpenChange={(o) => { if (!o) setActiveCondition(null); }}
        condition={activeCondition}
      />
    </section>
  );
}
